import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Ledger as LedgerComponent } from '@sg/garnish';
import type { Ledger } from '@sg/graphql-schema';

export const useLedgerLineItems = (props: UseLedgerItemsProps) => {
  const {
    ledger,
    showTaxes,
    showReward,
    showTotal,
    showTip,
    orderStatusRewardDiscountNote,
  } = props;

  const { formatMessage, formatNumber } = useIntl();

  const formatPrice = useMemo(
    () => (value: number, currency: string) =>
      String(
        formatNumber(Number(value) / 100, {
          style: 'currency',
          currency: String(currency),
        }),
      ),
    [formatNumber],
  );

  return useMemo(() => {
    const total = sumLedgerTotal(ledger);

    const subtotalItem: LedgerItem = {
      amount: formatPrice(ledger?.subtotal ?? 0, 'USD'),
      label: formatMessage(messages.subtotalLabel),
    };

    const taxesItem: LedgerItem = {
      amount: `${formatPrice(ledger?.tax ?? 0, 'USD')}`,
      label: formatMessage(messages.taxLabel),
    };

    const feesItems =
      ledger?.fees
        ?.filter((fee) => fee.amount)
        .map((fee) => ({
          label: fee.name,
          amount: formatPrice(fee.amount ?? 0, 'USD'),
          helperText: fee.description ?? undefined,
        })) ?? [];

    const appliedRewardItem: LedgerItem = {
      label: formatMessage(messages.reward),
      amount: formatMessage(messages.rewardCalculatedAtCheckout),
    };

    const tipItem: LedgerItem = {
      label: 'Tip',
      amount: formatPrice(ledger?.tip ?? 0, 'USD'),
      helperText: undefined,
    };

    const creditsItems =
      ledger?.credits
        ?.filter((credit) => credit.amount > 0)
        .map((credit) => ({
          label: credit.name,
          amount: `-${formatPrice(credit.amount ?? 0, 'USD')}`,
          helperText: credit.description ?? undefined,
          palette: 'success' as never,
        })) ?? [];

    const discountsItems =
      ledger?.discounts
        ?.filter((discount) => discount.amount)
        .map((discount) => ({
          label: discount.name,
          amount: `-${formatPrice(discount.amount ?? 0, 'USD')}`,
          helperText: discount.description
            ? orderStatusRewardDiscountNote
            : undefined,
          palette: 'success' as never,
        })) ?? [];

    const totalItem: LedgerItem = {
      label: formatMessage(messages.totalLabel),
      amount: `${formatPrice(total ?? 0, 'USD')}`,
      bold: true,
      testID: 'ledger.total',
    };

    return [
      subtotalItem,
      showTaxes && taxesItem,
      ...feesItems,
      !showReward && appliedRewardItem,
      showTip && tipItem,
      ...(showReward ? discountsItems : []),
      ...creditsItems,
      showTotal && totalItem,
    ].filter(Boolean) as readonly LedgerItem[];
  }, [
    ledger,
    formatPrice,
    formatMessage,
    showTaxes,
    showReward,
    showTip,
    showTotal,
    orderStatusRewardDiscountNote,
  ]);
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const sumLedgerTotal = (ledger: Partial<Ledger> | undefined): number => {
  if (!ledger) return 0;

  const ledgerTotal =
    Number(ledger.subtotal ?? 0) +
    Number(ledger.tax ?? 0) +
    Number(ledger.tip ?? 0) +
    Number(ledger.feesTotal ?? 0) -
    Number(ledger.creditsTotal ?? 0) -
    Number(ledger.discountsTotal ?? 0);

  return ledgerTotal;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  subtotalLabel: {
    defaultMessage: 'Subtotal',
    description: 'Order Status | Ledger | Subtotal',
  },
  taxLabel: {
    defaultMessage: 'Tax',
    description: 'Order Status | Ledger | Tax',
  },
  reward: {
    defaultMessage: 'Reward',
    description: 'Order Status | Ledger | Reward',
  },
  rewardCalculatedAtCheckout: {
    defaultMessage: 'Calculated at checkout',
    description: 'Order Status | Ledger | Reward Calculated at Checkout',
  },
  totalLabel: {
    defaultMessage: 'Total',
    description: 'Order Status | Ledger | Total',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type UseLedgerItemsProps = {
  ledger: Partial<Ledger> | undefined;
  showTip?: boolean;
  showReward?: boolean;
  showTaxes?: boolean;
  showTotal?: boolean;
  orderStatusRewardDiscountNote?: string;
};

type LedgerItem = ComponentProps<typeof LedgerComponent>['items'][0];
